/**
 * Toast Notification Utilities
 * Themed toast notifications for MyPortal
 */

import toast from 'react-hot-toast';

// Custom toast styles matching the app theme
const toastStyles = {
  success: {
    background: '#10B981',
    color: '#fff',
    iconTheme: {
      primary: '#10B981',
      secondary: '#fff',
    },
  },
  error: {
    background: '#EF4444',
    color: '#fff',
    iconTheme: {
      primary: '#EF4444',
      secondary: '#fff',
    },
  },
  warning: {
    background: '#F59E0B',
    color: '#fff',
    iconTheme: {
      primary: '#F59E0B',
      secondary: '#fff',
    },
  },
  info: {
    background: '#9D4EDD',
    color: '#fff',
    iconTheme: {
      primary: '#9D4EDD',
      secondary: '#fff',
    },
  },
};

/**
 * Show success toast
 */
export const showSuccess = (message: string, duration: number = 3000) => {
  toast.success(message, {
    duration,
    style: toastStyles.success,
  });
};

/**
 * Show error toast
 */
export const showError = (message: string, duration: number = 5000) => {
  toast.error(message, {
    duration,
    style: toastStyles.error,
  });
};

/**
 * Show warning toast
 */
export const showWarning = (message: string, duration: number = 4000) => {
  toast(message, {
    duration,
    icon: '⚠️',
    style: toastStyles.warning,
  });
};

/**
 * Show info toast
 */
export const showInfo = (message: string, duration: number = 3000) => {
  toast(message, {
    duration,
    icon: 'ℹ️',
    style: toastStyles.info,
  });
};

/**
 * Show loading toast (returns toast ID to dismiss later)
 */
export const showLoading = (message: string = 'Loading...') => {
  return toast.loading(message, {
    style: {
      background: '#1A1F3A',
      color: '#E6E8F0',
    },
  });
};

/**
 * Dismiss a specific toast or all toasts
 */
export const dismissToast = (toastId?: string) => {
  if (toastId) {
    toast.dismiss(toastId);
  } else {
    toast.dismiss();
  }
};

/**
 * Promise-based toast (shows loading, then success/error)
 */
export const showPromise = <T,>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string;
    error: string;
  }
) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading,
      success: messages.success,
      error: messages.error,
    },
    {
      success: {
        style: toastStyles.success,
      },
      error: {
        style: toastStyles.error,
      },
      loading: {
        style: {
          background: '#1A1F3A',
          color: '#E6E8F0',
        },
      },
    }
  );
};

// Specific toast helpers for common actions
export const toastHelpers = {
  // File operations
  fileUploaded: (filename: string) => {
    showSuccess(`📤 ${filename} uploaded successfully!`);
  },

  fileDeleted: () => {
    showSuccess('🗑️ File deleted successfully');
  },

  fileRenamed: () => {
    showSuccess('✏️ File renamed successfully');
  },

  fileMoved: () => {
    showSuccess('📦 File moved successfully');
  },

  folderMoved: () => {
    showSuccess('📦 Folder moved successfully');
  },

  itemsMoved: (count: number) => {
    showSuccess(`📦 ${count} item${count !== 1 ? 's' : ''} moved successfully`);
  },

  moveFailed: () => {
    showError('❌ Failed to move item');
  },

  fileDownloaded: (filename: string) => {
    showSuccess(`⬇️ ${filename} download started`);
  },

  // Folder operations
  folderCreated: (folderName: string) => {
    showSuccess(`📁 Folder "${folderName}" created`);
  },

  folderDeleted: () => {
    showSuccess('🗑️ Folder deleted successfully');
  },

  // Share operations
  linkCopied: () => {
    showSuccess('🔗 Share link copied to clipboard!');
  },

  linkGenerated: () => {
    showSuccess('✅ Share link generated (expires in 24h)');
  },

  // Tag operations
  tagsSaved: () => {
    showSuccess('🏷️ Tags saved successfully');
  },

  tagLimitReached: (limit: number) => {
    showWarning(`⚠️ Maximum ${limit} tags allowed`);
  },

  tagAlreadyExists: () => {
    showInfo('ℹ️ This tag is already added');
  },

  // Selection operations
  itemsSelected: (count: number) => {
    showInfo(`✓ ${count} item${count !== 1 ? 's' : ''} selected`);
  },

  bulkDownloadStarted: (count: number) => {
    showInfo(`⬇️ Downloading ${count} file${count !== 1 ? 's' : ''}...`);
  },

  bulkDownloadComplete: (count: number) => {
    showSuccess(`✅ ${count} file${count !== 1 ? 's' : ''} downloaded successfully!`);
  },

  // Error messages
  uploadFailed: (error?: string) => {
    showError(`❌ Upload failed${error ? ': ' + error : ''}`);
  },

  deleteFailed: () => {
    showError('❌ Failed to delete file');
  },

  networkError: () => {
    showError('❌ Network error. Please check your connection.');
  },

  authError: () => {
    showError('❌ Authentication error. Please sign in again.');
  },

  // Storage warnings
  storageLow: (percentage: number) => {
    showWarning(`⚠️ Storage ${percentage}% full. Consider deleting old files.`);
  },

  storageFull: () => {
    showError('❌ Storage full! Delete some files to continue uploading.');
  },

  // Generic messages
  actionCancelled: () => {
    showInfo('Action cancelled');
  },

  featureComingSoon: () => {
    showInfo('🚀 Feature coming soon!');
  },
};

export default {
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  loading: showLoading,
  dismiss: dismissToast,
  promise: showPromise,
  ...toastHelpers,
};
